/**
 * A `EnumSearchWidgets` renders all the `EnumSearchField` according to the
 * `field_schema` objects returned in the `fields` field from a `/api/v1/search` endpoint.
 * Notice that all `fields_schema` objects which are enum search fields are aggregated
 * into a list and rendered together in this widget
 */
import React from 'react';
import immutable from 'immutable';

import EnumSearchField from './EnumSearchField';
import SearchFieldsActions from 'actions/search/SearchFieldsActions';

require('styles/search/EnumSearchField.styl');

class EnumSearchWidgets extends React.Component {
  /**
   * Trigger `SearchFieldsActions.updateEnumSearchField` to update
   * `SearchStore.getState().get('searchExpression')` with `searchField` and `value`
   * @param  {records/Field} searchField A search field object
   * @param  {string} value       A string that specifies the value for an option in
   *                              `searchField`
   */
  onEnumChange(searchField, value) {
    SearchFieldsActions.updateEnumSearchField(searchField, value);
    const state = immutable.Map({
      name: searchField.name,
      type: searchField.type,
      value: value,
    });
    SearchFieldsActions.updateSearchFieldsState({
      state: state,
    });
  }

  /**
   * Return the value of searchField `sf` in
   * `SearchFieldsStore.getState().get('searchFieldsState')`
   * @param  {records/Field} sf A search field record
   * @return {string}    A option.value string for `sf` or null
   */
  getSearchFieldStateValue(sf) {
    const sfs = this.props.searchFieldsState.get(sf.name);
    if (sfs) {
      return sfs.value;
    } else {
      return null;
    }
  }

  /**
   * Return the `toggled` boolean value for a `searchField`
   * @param  {immutable/Record} searchField A search field
   * @return {boolean}             Whether the enum search field is toggled or not
   */
  getEnumSearchFieldsToggled(searchField) {
    if (this.props.searchFieldsToggled) {
      return this.props.searchFieldsToggled.get(searchField.name);
    }
    return false;
  }

  /**
   * Render a enum search field for the provided search field.
   * @param  {searchField} The search field to render.
   * @return {JSX} A enum search widget.
   */
  renderComponent(sf) {
    return (
      <EnumSearchField
        key={sf.name}
        searchField={sf}
        toggled={this.getEnumSearchFieldsToggled(sf)}
        onEnumChange={this.onEnumChange.bind(this)}
        updateLastToggledOnSearchWidget={this.props.updateLastToggledOnSearchWidget}
        shouldFocus={
          this.props.shouldRenderToggled &&
          this.getEnumSearchFieldsToggled(sf) &&
          sf.name === this.props.lastToggledOnSearchWidget
        }
        shouldSortByToggled={this.props.shouldSortByToggled}
        value={this.getSearchFieldStateValue(sf)}
      />
    );
  }

  /**
   * Returns a boolean that indicates whether the Search Field should not be rendered.
   * @return {boolean} When true indicates that the Search Field should not be rendered.
   */
  shouldNotRenderComponent(sf) {
    return !(
      (this.props.shouldRenderToggled && this.getEnumSearchFieldsToggled(sf)) ||
      (this.props.shouldRenderNotToggled && !this.getEnumSearchFieldsToggled(sf))
    );
  }

  render() {
    const searchFields = this.props.searchFields.sort(sf => sf.order);
    return (
      <div className="search-widget">
        {searchFields
          .filterNot(this.shouldNotRenderComponent, this)
          .map(this.renderComponent, this)
          .toArray()}
      </div>
    );
  }
}

export default EnumSearchWidgets;
